'use client'

import { useEffect } from 'react';
import "vanilla-cookieconsent/dist/cookieconsent.css";
import * as CookieConsent from "vanilla-cookieconsent";

// Custom event for cookie consent ready
const COOKIE_CONSENT_READY = 'cookieConsentReady';

export function CookieConsentProvider() {
  useEffect(() => {
    if (typeof window !== 'undefined') {
      console.log('Initializing CookieConsent');

      const reloadScript = (script: HTMLScriptElement) => {
        console.log('Reloading script:', script.id);
        const newScript = document.createElement('script');
        Array.from(script.attributes).forEach(attr => {
          newScript.setAttribute(attr.name, attr.value);
        });
        newScript.type = 'text/javascript';
        if (script.src) {
          newScript.src = script.src;
        } else {
          newScript.textContent = script.textContent;
        }
        script.parentNode?.replaceChild(newScript, script);
      };

      // First, make sure all scripts are disabled
      document.querySelectorAll('script[data-category]').forEach(script => {
        script.setAttribute('type', 'text/plain');
      });

      const reloadAcceptedScripts = () => {
        const status = CookieConsent.getConfig('categories');
        if (status) {
          Object.entries(status).forEach(([category, config]) => {
            if (config.enabled) {
              document.querySelectorAll(`script[data-category="${category}"]`).forEach(script => {
                reloadScript(script as HTMLScriptElement);
              });
            }
          });
        }
      };

      // Initialize cookie consent
      CookieConsent.run({
        categories: {
          necessary: {
            enabled: true,
            readOnly: true
          },
          analytics: {
            enabled: false,
            readOnly: false,
            autoClear: {
              cookies: [
                {
                  name: '_ga', // Google Analytics
                },
                {
                  name: 'ph_*' // PostHog
                }
              ]
            }
          },
          advertising: {
            enabled: false,
            readOnly: false,
            autoClear: {
              cookies: [
                {
                  name: '_fbp' // Facebook
                },
                {
                  name: '_tt_*' // TikTok
                }
              ]
            }
          },
          optional: {
            enabled: false,
            readOnly: false,
            autoClear: {
              cookies: [
                {
                  name: 'freshworks_*' // Freshworks
                }
              ]
            }
          }
        },
        services: {
          ga: {
            label: 'Google Analytics',
            category: 'analytics',
            onAccept: () => {
              console.log('GA accepted');
              document.querySelectorAll('script[data-service="ga"]').forEach(script => {
                reloadScript(script as HTMLScriptElement);
              });
            },
            onReject: () => {
              document.querySelectorAll('script[data-service="ga"]').forEach(script => {
                script.setAttribute('type', 'text/plain');
              });
            }
          },
          posthog: {
            label: 'PostHog Analytics',
            category: 'analytics',
            onAccept: () => {
              console.log('PostHog accepted');
              document.querySelectorAll('script[data-service="posthog"]').forEach(script => {
                reloadScript(script as HTMLScriptElement);
              });
            },
            onReject: () => {
              document.querySelectorAll('script[data-service="posthog"]').forEach(script => {
                script.setAttribute('type', 'text/plain');
              });
            }
          },
          tiktok: {
            label: 'TikTok Pixel',
            category: 'advertising',
            onAccept: () => {
              console.log('TikTok accepted');
              document.querySelectorAll('script[data-service="tiktok"]').forEach(script => {
                reloadScript(script as HTMLScriptElement);
              });
            },
            onReject: () => {
              document.querySelectorAll('script[data-service="tiktok"]').forEach(script => {
                script.setAttribute('type', 'text/plain');
              });
            }
          },
          facebook: {
            label: 'Meta Pixel',
            category: 'advertising',
            onAccept: () => {
              console.log('Facebook accepted');
              document.querySelectorAll('script[data-service="facebook"]').forEach(script => {
                reloadScript(script as HTMLScriptElement);
              });
            },
            onReject: () => {
              document.querySelectorAll('script[data-service="facebook"]').forEach(script => {
                script.setAttribute('type', 'text/plain');
              });
            }
          },
          freshdesk: {
            label: 'Freshdesk Support Widget',
            category: 'optional',
            onAccept: () => {
              console.log('Freshdesk accepted');
              document.querySelectorAll('script[data-service="freshdesk"]').forEach(script => {
                reloadScript(script as HTMLScriptElement);
              });
            },
            onReject: () => {
              document.querySelectorAll('script[data-service="freshdesk"]').forEach(script => {
                script.setAttribute('type', 'text/plain');
              });
            }
          }
        },
        guiOptions: {
          consentModal: {
            layout: 'box inline',
            position: 'bottom left',
            equalWeightButtons: true,
            flipButtons: false
          },
          preferencesModal: {
            layout: 'box',
            position: 'right',
            equalWeightButtons: true,
            flipButtons: false
          }
        },
        onFirstConsent: ({ cookie }) => {
          console.log('First consent given:', cookie.categories);
          reloadAcceptedScripts();
        },
        onConsent: ({ cookie }) => {
          console.log('Consent updated:', cookie.categories);
          reloadAcceptedScripts();
        },
        onChange: ({ changedCategories, changedServices }) => {
          console.log('Consent categories changed:', changedCategories);
          console.log('Changed services:', changedServices);
        },
        language: {
          default: 'en',
          translations: {
            en: {
              consentModal: {
                title: 'Cookie Preferences',
                description: 'We use cookies to enhance your browsing experience, analyze our traffic, and provide personalized advertising. Click "Accept all" to consent to all cookies or "Preferences" to choose specific categories.',
                acceptAllBtn: 'Accept all',
                showPreferencesBtn: 'Preferences',
                closeIconLabel: 'Close',
              },
              preferencesModal: {
                title: 'Cookie Preferences',
                acceptAllBtn: 'Accept all',
                acceptNecessaryBtn: 'Accept necessary only',
                savePreferencesBtn: 'Save preferences',
                closeIconLabel: 'Close',
                sections: [
                  {
                    title: 'Cookie Usage',
                    description: 'We use cookies to ensure basic functionality and to analyze our traffic with anonymized data.'
                  },
                  {
                    title: 'Strictly Necessary Cookies',
                    description: 'These cookies are essential for the website to function properly.',
                    linkedCategory: 'necessary'
                  },
                  {
                    title: 'Analytics Cookies',
                    description: 'These cookies help us understand how visitors interact with our website.',
                    linkedCategory: 'analytics'
                  },
                  {
                    title: 'Advertising Cookies',
                    description: 'These cookies are used to show you relevant advertising.',
                    linkedCategory: 'advertising'
                  },
                  {
                    title: 'Optional Features',
                    description: 'These cookies enable additional functionality like chat support.',
                    linkedCategory: 'optional'
                  }
                ]
              }
            }
          }
        }
      }).then(() => {
        // After initialization, check for existing consent and emit ready event
        setTimeout(() => {
          reloadAcceptedScripts();
          // Dispatch event to notify that cookie consent is ready
          window.dispatchEvent(new Event(COOKIE_CONSENT_READY));
        }, 100);
      });
    }

    return () => {
      // Cleanup if needed
      if (typeof window !== 'undefined') {
        window.removeEventListener(COOKIE_CONSENT_READY, () => {});
      }
    };
  }, []);

  return null;
} 